import { loadStaticLoginMessages, type StaticLoginPageProps } from '@/lib/seo/static-login-data';
import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';

import { SeoGuideArticlePage } from '@/components/public-site/SeoGuidePages';
import { StaticSeoGuideHead } from '@/components/public-site/StaticSeoGuideHead';
import { buildGuideJsonLd, escapeJsonLd } from '@/lib/seo/guide-json-ld';
import {
  SEO_GUIDE_CONFIGS,
  getSeoGuide,
  getSeoGuides,
  type SeoGuideArticle,
} from '@/lib/seo/guides';

interface GuideArticleProps {
  locale: 'en';
  guide: SeoGuideArticle;
  relatedGuides: SeoGuideArticle[];
  jsonLd: string;
}

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: SEO_GUIDE_CONFIGS.map(({ slug }) => ({ params: { slug } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<GuideArticleProps & StaticLoginPageProps> = async ({ params }) => {
  const guide = getSeoGuide(String(params?.slug ?? ''), 'en');
  if (!guide) return { notFound: true };

  return {
    props: {
      locale: 'en',
      loginMessages: await loadStaticLoginMessages('en'),
      guide,
      relatedGuides: getSeoGuides('en').filter((item) => item.slug !== guide.slug).slice(0, 2),
      jsonLd: escapeJsonLd(buildGuideJsonLd(guide, 'en')),
    },
    revalidate: 600,
  };
};

export default function EnglishGuideArticle(
  { locale, guide, relatedGuides, jsonLd }: InferGetStaticPropsType<typeof getStaticProps>,
) {
  return (
    <>
      <StaticSeoGuideHead locale={locale} guide={guide} />
      <SeoGuideArticlePage locale={locale} guide={guide} relatedGuides={relatedGuides} jsonLd={jsonLd} />
    </>
  );
}
